import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    Button,
    Slider,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface SatisfactionSurveyProps {
    categoryId: string;
    categoryName: string;
    onComplete?: () => void;
}

const SatisfactionSurvey: React.FC<SatisfactionSurveyProps> = ({ categoryId, categoryName, onComplete }) => {
    const { showToast } = useToast();
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Form state
    const [trainingPace, setTrainingPace] = useState('');
    const [pedagogicalProgression, setPedagogicalProgression] = useState('');
    const [overallPedagogy, setOverallPedagogy] = useState('');
    const [documentationQuality, setDocumentationQuality] = useState<number>(3);
    const [courseRelevance, setCourseRelevance] = useState<number>(3);
    const [recommendationScore, setRecommendationScore] = useState<number>(5);

    useEffect(() => {
        checkExistingSurvey();
    }, [categoryId]);

    const checkExistingSurvey = async () => {
        try {
            const res = await api.get(`/api/satisfaction/category/${categoryId}`);
            if (res.data && res.data.completedAt) {
                // User already submitted survey
                setSubmitted(true);
                setTrainingPace(res.data.trainingPace || '');
                setPedagogicalProgression(res.data.pedagogicalProgression || '');
                setOverallPedagogy(res.data.overallPedagogy || '');
                setDocumentationQuality(res.data.documentationQuality || 3);
                setCourseRelevance(res.data.courseRelevance || 3);
                setRecommendationScore(res.data.recommendationScore || 5);
            } else if (res.data) {
                // Survey exists (e.g. opened by CRON) but not yet completed
                setSubmitted(false);
                // Pre-fill if some data already exists (though unlikely for CRON)
                if (res.data.trainingPace) setTrainingPace(res.data.trainingPace);
                if (res.data.pedagogicalProgression) setPedagogicalProgression(res.data.pedagogicalProgression);
                if (res.data.overallPedagogy) setOverallPedagogy(res.data.overallPedagogy);
            }
        } catch (err) {
            console.error("Error checking existing survey", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!trainingPace || !pedagogicalProgression || !overallPedagogy.trim()) {
            showToast("Veuillez répondre à toutes les questions", "error");
            return;
        }

        setSaving(true);
        try {
            await api.post('/api/satisfaction', {
                categoryId,
                trainingPace,
                pedagogicalProgression,
                overallPedagogy,
                documentationQuality,
                courseRelevance,
                recommendationScore
            });

            showToast("Merci pour votre retour !", "success");
            setSubmitted(true);
            if (onComplete) onComplete();
        } catch (err) {
            console.error("Error submitting survey", err);
            showToast("Erreur lors de l'envoi du questionnaire", "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ p: 3 }}>Chargement...</Box>;
    }

    if (submitted) {
        return (
            <Paper sx={{ p: 4, borderRadius: 4, bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 40 }} />
                    <Typography variant="h6" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 700 }}>
                        Questionnaire de satisfaction complété
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontFamily: 'Quicksand, sans-serif', color: 'text.secondary' }}>
                    Merci d'avoir pris le temps de répondre à ce questionnaire. Vos retours nous aident à améliorer continuellement la qualité de nos formations.
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 4, borderRadius: 4, mt: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 700, mb: 3 }}>
                Questionnaire de satisfaction - {categoryName}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {/* Question 1: Training Pace */}
                <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600, mb: 2 }}>
                        Actuellement, comment évalueriez-vous le rythme de votre formation (Temps vis-à-vis du travail) ?
                    </FormLabel>
                    <RadioGroup value={trainingPace} onChange={(e) => setTrainingPace(e.target.value)}>
                        <FormControlLabel value="Trop rapide" control={<Radio />} label={<Typography sx={{ fontFamily: 'Quicksand, sans-serif' }}>Trop rapide</Typography>} />
                        <FormControlLabel value="Rapide" control={<Radio />} label={<Typography sx={{ fontFamily: 'Quicksand, sans-serif' }}>Rapide</Typography>} />
                        <FormControlLabel value="Adapté" control={<Radio />} label={<Typography sx={{ fontFamily: 'Quicksand, sans-serif' }}>Adapté</Typography>} />
                        <FormControlLabel value="Lent" control={<Radio />} label={<Typography sx={{ fontFamily: 'Quicksand, sans-serif' }}>Lent</Typography>} />
                        <FormControlLabel value="Trop lent" control={<Radio />} label={<Typography sx={{ fontFamily: 'Quicksand, sans-serif' }}>Trop lent</Typography>} />
                    </RadioGroup>
                </FormControl>

                {/* Question 2: Pedagogical Progression */}
                <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600, mb: 2 }}>
                        Avez-vous trouvé la progression pédagogique de cette partie : {categoryName} pertinente ?
                    </FormLabel>
                    <RadioGroup value={pedagogicalProgression} onChange={(e) => setPedagogicalProgression(e.target.value)}>
                        <FormControlLabel value="Oui" control={<Radio />} label={<Typography sx={{ fontFamily: 'Quicksand, sans-serif' }}>Oui</Typography>} />
                        <FormControlLabel value="Partiellement" control={<Radio />} label={<Typography sx={{ fontFamily: 'Quicksand, sans-serif' }}>Partiellement</Typography>} />
                        <FormControlLabel value="Non" control={<Radio />} label={<Typography sx={{ fontFamily: 'Quicksand, sans-serif' }}>Non</Typography>} />
                    </RadioGroup>
                </FormControl>

                {/* Question 3: Overall Pedagogy */}
                <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600, mb: 2 }}>
                        Actuellement et globalement, que pensez-vous de la pédagogie de votre formation ?
                    </FormLabel>
                    <TextField
                        multiline
                        rows={4}
                        value={overallPedagogy}
                        onChange={(e) => setOverallPedagogy(e.target.value)}
                        placeholder="Partagez votre avis..."
                        fullWidth
                        InputProps={{ style: { fontFamily: 'Quicksand, sans-serif' } }}
                    />
                </FormControl>

                {/* Question 4a: Documentation Quality */}
                <Box>
                    <Typography variant="body1" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600, mb: 2 }}>
                        La documentation et les supports pédagogiques de cette catégorie ({categoryName}) sont adaptés
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'Quicksand, sans-serif', color: 'text.secondary', display: 'block', mb: 1 }}>
                        1 = Pas du tout, 5 = Tout à fait
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Slider
                            value={documentationQuality}
                            onChange={(_, value) => setDocumentationQuality(value as number)}
                            min={1}
                            max={5}
                            marks
                            step={1}
                            valueLabelDisplay="on"
                            sx={{ flexGrow: 1 }}
                        />
                    </Box>
                </Box>

                {/* Question 4b: Course Relevance */}
                <Box>
                    <Typography variant="body1" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600, mb: 2 }}>
                        Les cours de cette catégorie ({categoryName}) sont pertinents
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'Quicksand, sans-serif', color: 'text.secondary', display: 'block', mb: 1 }}>
                        1 = Pas du tout, 5 = Tout à fait
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Slider
                            value={courseRelevance}
                            onChange={(_, value) => setCourseRelevance(value as number)}
                            min={1}
                            max={5}
                            marks
                            step={1}
                            valueLabelDisplay="on"
                            sx={{ flexGrow: 1 }}
                        />
                    </Box>
                </Box>

                {/* Question 5: Recommendation Score */}
                <Box>
                    <Typography variant="body1" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600, mb: 2 }}>
                        Sur une échelle de 1 à 10, recommanderiez-vous, globalement, cette formation autour de vous ?
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'Quicksand, sans-serif', color: 'text.secondary', display: 'block', mb: 1 }}>
                        1 = Pas du tout, 10 = Tout à fait
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Slider
                            value={recommendationScore}
                            onChange={(_, value) => setRecommendationScore(value as number)}
                            min={1}
                            max={10}
                            marks
                            step={1}
                            valueLabelDisplay="on"
                            sx={{ flexGrow: 1 }}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleSubmit}
                        disabled={saving}
                        sx={{
                            fontFamily: 'Quicksand, sans-serif',
                            fontWeight: 600,
                            textTransform: 'none',
                            px: 4,
                            borderRadius: 2
                        }}
                    >
                        {saving ? 'Envoi...' : 'Envoyer mes réponses'}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default SatisfactionSurvey;
